// 欢迎登录页面 - 1:1还原Pixso设计稿
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../app/hooks';
import { setLanguage } from '../data/languageSlice';
import { useWalletConnect } from '../hooks/useWalletConnect';
import { getNonce, login } from '../api/auth';
import { getAgreements, Agreement } from '../api/agreement';
import { setLoginSuccess } from '../data/accountSlice';
import { getImageUrl } from '../utils/assets';
import toast from 'react-hot-toast';
import AgreementModal from './AgreementModal';
import './LoginWelcomePage.css';

// Global lock to prevent double login attempts (e.g. React Strict Mode or rapid re-renders)
let loginLock: string | null = null;

interface LoginWelcomePageProps {
  onComplete: () => void;
}

const LoginWelcomePage: React.FC<LoginWelcomePageProps> = ({ onComplete }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { isConnected, account, connectWallet, connectWalletWithQr, signMessage, disconnectWallet } = useWalletConnect();

  const [agreedToTerms, setAgreedToTerms] = useState(localStorage.getItem('pending_agreement') === 'all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [agreements, setAgreements] = useState<Agreement[]>([]);

  // 语言切换
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    dispatch(setLanguage(lang));
    localStorage.setItem('language', lang);
    fetchAgreements(); // Fetch agreements in new language
  };

  const fetchAgreements = async () => {
    try {
      const data = await getAgreements();
      setAgreements(data);
    } catch (error) {
      console.error('Failed to fetch agreements', error);
    }
  };

  useEffect(() => {
    fetchAgreements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const setRem = () => {
      const html = document.documentElement as HTMLElement;
      let width = html.clientWidth;
      if (width > 750) width = 750;
      html.style.fontSize = width / 7.5 + 'px';
    };
    setRem();
    const onResize = () => setRem();
    const onPageshow = (e: any) => { if (e && e.persisted) setRem(); };
    window.addEventListener('resize', onResize);
    window.addEventListener('pageshow', onPageshow as any);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pageshow', onPageshow as any);
    };
  }, []);

  // Handle Login Flow when Connected
  useEffect(() => {
    const performLogin = async () => {
      // Check for token first to avoid unnecessary login attempts
      const token = localStorage.getItem('jwt_token');

      if (token) {
        return;
      }

      if (isConnected && account && !isLoggingIn) {
        // Prevent duplicate login attempts for the same account
        if (loginLock === account) {
          console.log('Login already in progress for', account);
          return;
        }

        // Auto-check agreement if wallet is already connected (e.g. re-login after 401)
        if (!agreedToTerms) {
          setAgreedToTerms(true);
          localStorage.setItem('pending_agreement', 'all');
        }

        loginLock = account;
        setIsLoggingIn(true);
        const toastId = toast.loading(t('welcome.loggingIn', '正在登錄...'));

        try {
          // 1. Get Nonce
          const { nonce, message } = await getNonce(account);

          // 2. Sign Message
          const signature = await signMessage(message);

          // 3. Login
          const loginResponse = await login(account, signature, nonce);

          // 4. Save Token & Dispatch
          dispatch(setLoginSuccess(loginResponse));
          
          toast.success(t('welcome.loginSuccess', '登錄成功'), { id: toastId });
          
          // App.tsx will detect token change and switch state
        } catch (error: any) {
          console.error('Login failed:', error);
          
          // Handle User Rejected Request specifically
          const errorMessage = error?.message || '';
          if (
            errorMessage.includes('User rejected') || 
            errorMessage.includes('UserRejectedRequestError') ||
            error?.name === 'UserRejectedRequestError'
          ) {
            toast.error(t('welcome.userRejected', '用戶取消了操作'), { id: toastId });
          } else if (
            errorMessage.includes('Connector not connected') ||
            errorMessage.includes('ConnectorNotConnectedError') ||
            error?.name === 'ConnectorNotConnectedError'
          ) {
            toast.error(t('welcome.connectorNotConnected', '錢包連接已斷開，請重新連接'), { id: toastId });
          } else {
            toast.error(t('welcome.loginFailed', '登錄失敗') + ': ' + errorMessage, { id: toastId });
          }
          
          // If login fails, should we disconnect?
          // Maybe user rejected signature.
          await disconnectWallet();
        } finally {
          loginLock = null;
          setIsLoggingIn(false);
        }
      }
    };

    performLogin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, account]);

  const getFontClass = () => {
    if (i18n.language === 'en') return 'font-en';
    if (i18n.language === 'pt') return 'font-pt';
    return 'font-zh';
  };

  return (
    <div className={`login-scroll-container ${getFontClass()}`}>
      <div className="login-canvas">
        <div className="login-frame">
          <div className="bg-empty" />
          {/*
          <div className="bg-main" style={{ backgroundImage: `url(${getImageUrl('welcome/bg-layer-1.png')})` }} />
          */
          }
          <div className="bg-overlay" style={{ backgroundImage: `url(${getImageUrl('welcome/bg-layer-2.png')})` }} />
          <div className="character" style={{ backgroundImage: `url(${getImageUrl('welcome/title-decoration.png')})` }} />
          <div className="bottom-decoration" style={{ backgroundImage: `url(${getImageUrl('welcome/modal-background.png')})` }} />

          <div className="language-menu" style={{ backgroundImage: `url(${getImageUrl('welcome/lang-switcher-bg.png')})` }} />
          <button
            className="btn-chinese"
            onClick={() => handleLanguageChange('zh-TW')}
            style={{ backgroundImage: `url(${getImageUrl('welcome/lang-btn-zh.png')})` }}
          />
          <span className="chinese-text">繁體中文</span>
          <button
            className="btn-english"
            onClick={() => handleLanguageChange('en')}
            style={{ backgroundImage: `url(${getImageUrl('welcome/lang-btn-en.png')})` }}
          />
          <span className="english-text">English</span>
          <button
            className="btn-portuguese"
            onClick={() => handleLanguageChange('pt')}
            style={{ backgroundImage: `url(${getImageUrl('welcome/lang-btn-pt.png')})` }}
          />
          <span className="portuguese-text">Português</span>

      {/* 协议内容始终显示 */}
      <div className="agreement-group" style={{ opacity: 1 }}>
        <div className="agreement-container">
          <div className="agreement-panel" style={{ backgroundImage: `url(${getImageUrl('welcome/agreement-box.png')})` }} />
          <div 
            className="agreement-title" 
            onClick={() => {
              setShowAgreementModal(true);
              setIsModalOpen(true);
            }}
            style={{ cursor: 'pointer' }}
          >
            {t('welcome.agreementTitle')}
          </div>
          <div className="agreement-content">
            {agreements.length > 0 ? (
              agreements.map((item, index) => (
                <div key={index}>
                  <p><strong>{item.title}</strong></p>
                  <div dangerouslySetInnerHTML={{ __html: item.content }} />
                  <br/>
                </div>
              ))
            ) : (
              (t('welcome.agreementItems', { returnObjects: true }) as unknown as string[]).map((item, index) => (
                <p key={index}>{item}</p>
              ))
            )}
          </div>
          <div className="agreement-consent">
            <div 
              className="checkbox" 
              style={{ backgroundImage: `url(${getImageUrl(`welcome/${agreedToTerms ? 'checkbox-checked.png' : 'checkbox-unchecked.png'}`)})` }} 
              onClick={() => {
                const newState = !agreedToTerms;
                setAgreedToTerms(newState);
                if (newState) {
                  localStorage.setItem('pending_agreement', 'all');
                } else {
                  localStorage.removeItem('pending_agreement');
                }
              }} 
            />
            <span className="checkbox-text">{t('welcome.agreeCheckbox')}</span>
          </div>
        </div>

        {/* 按钮区域 */}
          <div className="agreement-actions">
            <div className="actions-buttons">
              <div
                className="btn-agree-bg"
                onClick={async () => {
                  if (!agreedToTerms) {
                    toast(t('welcome.pleaseAgree'));
                    return;
                  }
                  
                  // If already connected, manual connect is not needed (App will handle it)
                  if (isConnected) {
                    return;
                  }

                  setIsModalOpen(true);
                  try {
                    await connectWalletWithQr();
                  } finally {
                    setIsModalOpen(false);
                  }
                }}
                style={{ backgroundImage: `url(${getImageUrl(`welcome/${agreedToTerms ? 'btn-reject.png' : 'btn-agree.png' }`)})` }}
              >
                <span className="btn-agree-text">{t('welcome.connectWallet')}</span>
              </div>
              <div
                className="btn-reject-bg"
                onClick={() => {
                  setAgreedToTerms(false);
                  localStorage.removeItem('pending_agreement');
                }}
                style={{ backgroundImage: `url(${getImageUrl('welcome/btn-reject.png')})` }}
              >
                <span className="btn-reject-text">{t('common.cancel')}</span>
              </div>
            </div>
          </div>
      </div>
      
        </div>
      </div>

      <AgreementModal 
        visible={showAgreementModal} 
        onClose={() => {
          setShowAgreementModal(false);
          setIsModalOpen(false);
          // Sync state from modal (in case user agreed there)
          setAgreedToTerms(localStorage.getItem('pending_agreement') === 'all');
        }} 
      />
    </div>
  );
};

export default LoginWelcomePage;
